import * as APITypes from "../../../../api/cur8/generated-types/v1/Api";
import { makePath } from "../../../../api/url";
import { applyParams } from "../../../params";
import { applyPagination } from "../../pagination";
import { getJSON, postJSON, putJSON } from "../../request";
import { APIContext } from "../../types";

export type PatientAPI = ReturnType<typeof createPatientAPI>;

type QueryParams = Partial<APITypes.PatientDetailsSearchParams>;

const DEFAULT_QUERY_PARAMS: QueryParams = {
  pageSize: 25,
};

function applyOptions(params: URLSearchParams, options: QueryParams) {
  applyParams(params, {
    text: options.text,
  });
}

export function createPatientAPI({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
}: APIContext) {
  function queryPatients(options: QueryParams) {
    const path = makePath(["api", "v1", "patient"]);
    const url = buildURL(path);

    const effectiveOptions = {
      ...DEFAULT_QUERY_PARAMS,
      ...options,
    };
    applyOptions(url.searchParams, effectiveOptions);
    applyPagination(url.searchParams, effectiveOptions);

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.PatientDetails>(request);
  }

  function fetchPatient({ patientId }: { patientId: string }) {
    const path = makePath(["api", "v1", "patient", patientId]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.PatientDetails>(request);
  }

  function createPatient(createRequest: APITypes.CreatePatientRequest) {
    const path = makePath(["api", "v1", "patient"]);
    const url = buildURL(path);
    const request = postJSON(url, createRequest);
    return fetchJSON<APITypes.PatientDetails>(request);
  }

  function updatePatient(
    patientId: string,
    updateRequest: APITypes.UpdatePatientDetailsRequest
  ) {
    const path = makePath(["api", "v1", "patient", patientId]);
    const url = buildURL(path);
    const request = putJSON(url, updateRequest);
    return fetchJSON<APITypes.PatientDetails>(request);
  }

  function sendSignupLink(
    patientId: string,
    sendSignupLinkRequest: APITypes.SendSignupLinkRequest
  ) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "send-signup-link",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, sendSignupLinkRequest);
    return fetchJSON<APITypes.SendSignupLinkResponse>(request);
  }

  function fetchInferredPhoneNumbers(patientId: string) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "try-get-phone-number",
    ]);
    const url = buildURL(path);
    const request = getJSON(url);

    return fetchJSON<APITypes.PatientPhoneNumberResponse>(request);
  }

  function sendIncidentReport(
    patientId: string,
    visitId: string,
    incidentReport: APITypes.IncidentReport
  ) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "incident-report",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, incidentReport);
    return fetchJSON<APITypes.SendSignupLinkResponse>(request);
  }

  function fetchJournalNote({
    patientId,
    journalItemId,
  }: {
    patientId: string;
    journalItemId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "journal",
      journalItemId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.JournalNote>(request);
  }

  function sendSkinBookAppointment({
    patientId,
    scanId,
    appointmentStartTime,
    status,
  }: {
    patientId: string;
    scanId: string;
    appointmentStartTime: string;
    status: APITypes.SkinAppointmentStatus;
  }) {
    const path = makePath(["api", "v1", "patient", patientId, "skin-book"]);
    const url = buildURL(path);
    const body: APITypes.UpsertSkinBookTrackingRequest = {
      scanId,
      appointmentStartTime,
      status,
    };
    const request = postJSON(url, body);
    return fetchJSON<APITypes.SkinBookTracking>(request);
  }

  function createEphermeralToken(
    { patientId }: { patientId: string },
    { requestMethod }: { requestMethod: APITypes.RequestMethod }
  ) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "ephemeral-token",
    ]);
    const url = buildURL(path);

    applyParams(url.searchParams, {
      requestMethod,
    });

    const request = getJSON(url);
    return fetchJSON<APITypes.EphemeralTokenResponse>(request);
  }

  return {
    queryPatients,
    createPatient,
    fetchPatient,
    updatePatient,
    sendSignupLink,
    fetchInferredPhoneNumbers,
    sendIncidentReport,
    fetchJournalNote,
    sendSkinBookAppointment,
    createEphermeralToken,
  };
}
